import { useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { Check, MessageCircle, Upload, X } from "lucide-react";
import { toast } from "sonner";
import { useStore } from "@/context/StoreContext";
import { formatIDR } from "@/data/products";
import { WA_NUMBER } from "@/i18n";

const PAYMENT_METHODS = ["Bank Transfer", "QRIS", "DANA", "GoPay", "OVO"];

export default function CheckoutModal() {
  const { t, cart, cartTotal, checkoutOpen, setCheckoutOpen, clearCart } = useStore();
  const [form, setForm] = useState({ name: "", phone: "", address: "" });
  const [proof, setProof] = useState(null);
  const [payment, setPayment] = useState("QRIS");

  const close = () => setCheckoutOpen(false);
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const confirm = () => {
    if (cart.length === 0) {
      toast.error(t.checkout.errEmpty);
      return;
    }
    if (!form.name.trim() || !form.phone.trim() || !form.address.trim()) {
      toast.error(t.checkout.errRequired);
      return;
    }
    const lines = cart.map(
      (i, idx) => `${idx + 1}. ${i.name} (${i.strain}) x${i.qty} — ${formatIDR(i.price * i.qty)}`,
    );
    const msg = [
      t.wa.greeting,
      "",
      ...lines,
      "",
      `${t.wa.total}: ${formatIDR(cartTotal)}`,
      "",
      `${t.wa.name}: ${form.name}`,
      `${t.wa.phone}: ${form.phone}`,
      `${t.wa.address}: ${form.address}`,
      `${t.wa.payment}: ${payment}`,
      t.wa.proof,
      "",
      t.wa.closing,
    ].join("\n");
    window.open(`https://wa.me/${WA_NUMBER}?text=${encodeURIComponent(msg)}`, "_blank");
    clearCart();
    setForm({ name: "", phone: "", address: "" });
    setProof(null);
    close();
  };

  const inputCls =
    "w-full rounded-xl border border-white/15 bg-black/60 px-4 py-3 text-sm text-white placeholder:text-zinc-600 outline-none transition-colors focus:border-ember";

  return (
    <AnimatePresence>
      {checkoutOpen && (
        <>
          <motion.div
            data-testid="checkout-backdrop"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={close}
            className="fixed inset-0 z-[80] bg-black/75 backdrop-blur-sm"
          />
          <div className="fixed inset-0 z-[90] flex items-center justify-center p-4">
            <motion.div
              data-testid="checkout-modal"
              initial={{ opacity: 0, y: 40, scale: 0.97 }}
              animate={{ opacity: 1, y: 0, scale: 1 }}
              exit={{ opacity: 0, y: 40, scale: 0.97 }}
              transition={{ duration: 0.45, ease: [0.16, 1, 0.3, 1] }}
              className="max-h-[90vh] w-full max-w-lg overflow-y-auto rounded-3xl border border-white/10 bg-ink-soft"
            >
              <div className="flex items-start justify-between border-b border-white/10 px-7 py-6">
                <div>
                  <h3 className="font-display text-xl font-bold text-white">
                    {t.checkout.title}
                  </h3>
                  <p className="mt-1 text-xs text-mist">{t.checkout.subtitle}</p>
                </div>
                <button
                  data-testid="checkout-close-btn"
                  onClick={close}
                  className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full border border-white/15 text-mist transition-colors hover:border-ember hover:text-ember"
                  aria-label="Close checkout"
                >
                  <X size={15} />
                </button>
              </div>

              <div className="flex flex-col gap-5 px-7 py-6">
                <div>
                  <label className="mb-2 block font-mono text-[10px] uppercase tracking-[0.25em] text-zinc-500">
                    {t.checkout.name}
                  </label>
                  <input
                    data-testid="checkout-name-input"
                    value={form.name}
                    onChange={set("name")}
                    placeholder={t.checkout.namePh}
                    className={inputCls}
                  />
                </div>
                <div>
                  <label className="mb-2 block font-mono text-[10px] uppercase tracking-[0.25em] text-zinc-500">
                    {t.checkout.phone}
                  </label>
                  <input
                    data-testid="checkout-phone-input"
                    value={form.phone}
                    onChange={set("phone")}
                    placeholder={t.checkout.phonePh}
                    inputMode="tel"
                    className={inputCls}
                  />
                </div>
                <div>
                  <label className="mb-2 block font-mono text-[10px] uppercase tracking-[0.25em] text-zinc-500">
                    {t.checkout.address}
                  </label>
                  <textarea
                    data-testid="checkout-address-input"
                    value={form.address}
                    onChange={set("address")}
                    placeholder={t.checkout.addressPh}
                    rows={3}
                    className={`${inputCls} resize-none`}
                  />
                </div>

                <div>
                  <label className="mb-2 block font-mono text-[10px] uppercase tracking-[0.25em] text-zinc-500">
                    {t.checkout.proofLabel}
                  </label>
                  <label
                    data-testid="checkout-proof-upload"
                    className="flex cursor-pointer items-center gap-3 rounded-xl border border-dashed border-white/20 bg-black/40 px-4 py-3.5 transition-colors hover:border-ember/50"
                  >
                    <Upload size={15} className="shrink-0 text-ember" />
                    <span className="truncate text-sm text-mist">
                      {proof ? proof.name : t.checkout.proofNone}
                    </span>
                    <span className="ml-auto shrink-0 rounded-full border border-white/15 px-3 py-1 font-mono text-[10px] uppercase tracking-widest text-white">
                      {t.checkout.proofButton}
                    </span>
                    <input
                      type="file"
                      accept="image/*"
                      className="hidden"
                      onChange={(e) => setProof(e.target.files?.[0] || null)}
                    />
                  </label>
                  <p className="mt-2 text-[11px] text-zinc-500">{t.checkout.proofHint}</p>
                </div>

                <div>
                  <label className="mb-2 block font-mono text-[10px] uppercase tracking-[0.25em] text-zinc-500">
                    {t.checkout.payment}
                  </label>
                  <div className="grid grid-cols-2 gap-2 sm:grid-cols-3" data-testid="payment-methods">
                    {PAYMENT_METHODS.map((m) => (
                      <button
                        key={m}
                        data-testid={`payment-method-${m.toLowerCase().replace(/\s+/g, "-")}`}
                        onClick={() => setPayment(m)}
                        className={`flex items-center justify-center gap-1.5 rounded-xl border px-3 py-2.5 font-mono text-[11px] font-bold uppercase tracking-wider transition-all duration-300 ${
                          payment === m
                            ? "border-ember bg-ember/10 text-ember"
                            : "border-white/15 text-mist hover:border-white/30 hover:text-white"
                        }`}
                      >
                        {payment === m && <Check size={12} />}
                        {m}
                      </button>
                    ))}
                  </div>
                </div>

                <div className="rounded-2xl border border-white/10 bg-ink-card p-5">
                  <p className="mb-3 font-mono text-[10px] uppercase tracking-[0.25em] text-zinc-500">
                    {t.checkout.summary}
                  </p>
                  <ul className="flex flex-col gap-2">
                    {cart.map((i) => (
                      <li key={i.id} className="flex items-center justify-between gap-3 text-sm">
                        <span className="truncate text-white">
                          {i.name} <span className="text-zinc-500">x{i.qty}</span>
                        </span>
                        <span className="shrink-0 font-mono text-xs text-mist">
                          {formatIDR(i.price * i.qty)}
                        </span>
                      </li>
                    ))}
                  </ul>
                  <div className="mt-4 flex items-center justify-between border-t border-white/10 pt-4">
                    <span className="font-mono text-xs uppercase tracking-widest text-mist">
                      {t.checkout.total}
                    </span>
                    <span
                      data-testid="checkout-total"
                      className="font-display text-lg font-bold text-ember-light"
                    >
                      {formatIDR(cartTotal)}
                    </span>
                  </div>
                </div>

                <button
                  data-testid="checkout-confirm-btn"
                  onClick={confirm}
                  className="flex w-full items-center justify-center gap-3 rounded-full bg-ember py-4 font-mono text-xs font-bold uppercase tracking-[0.2em] text-black transition-all duration-300 hover:bg-ember-light hover:shadow-[0_0_32px_rgba(255,107,0,0.4)]"
                >
                  <MessageCircle size={15} />
                  {t.checkout.confirm}
                </button>
                <p className="text-center text-[11px] text-zinc-500">{t.checkout.confirmNote}</p>
              </div>
            </motion.div>
          </div>
        </>
      )}
    </AnimatePresence>
  );
}
